'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { HousingProperty } from '@eliadmin/core/src/api/housing/housing-types';
import { fetchHousingProperty } from '@eliadmin/core/src/api/housing/housing-api';
import { FormField, FormItem, FormMessage } from '@eliadmin/core/src/components/ui/form';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useFormContext } from 'react-hook-form';
import type {
  ReservationFormInputValues,
  ReservationFormValues,
} from '@eliadmin/core/src/components/reservation/reservation-form/reservation-form-schema';
import Image from 'next/image';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { FaCheck } from 'react-icons/fa6';
import { useEffect } from 'react';
import { computeHousingNightlyPrice, countNights } from './date-utils';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useQuery } from '@tanstack/react-query';

interface ReservationFormStep2Props {
  properties: HousingProperty[];
  isLoading: boolean;
  isError: boolean;
  onGoToStep: (step: number) => void;
}

export function ReservationFormStep2({
  properties,
  isLoading,
  isError,
  onGoToStep,
}: ReservationFormStep2Props) {
  const form = useFormContext<ReservationFormInputValues, any, ReservationFormValues>();

  const arrival = form.watch('arrival');
  const departure = form.watch('departure');
  const guestsCount = Number(form.watch('guests_count')) || 2;
  const selectedPropertyId = form.watch('property_id');

  const arrivalDate = arrival instanceof Date ? arrival : null;
  const departureDate = departure instanceof Date ? departure : null;
  const nights = arrivalDate && departureDate ? countNights(arrivalDate, departureDate) : 0;

  // If the previously selected property is no longer available for the
  // current date range (or none was selected yet), fall back to the first
  // property that the backend actually confirmed as free.
  useEffect(() => {
    if (isLoading) return;
    const stillAvailable = properties.some(p => p.id === selectedPropertyId);
    if (!stillAvailable) {
      form.setValue('property_id', properties[0]?.id as any, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, isLoading]);

  if (isLoading) {
    return (
      <div className="grid w-full grid-cols-12 gap-4">
        {[0, 1, 2].map(i => (
          <div
            key={i}
            className="col-span-12 h-[104px] w-full animate-pulse rounded-[24px] bg-[#F2EEE7] md:h-[104px]"
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-4 rounded-[24px] border border-border p-6 text-center">
        <p className="text-base text-secondary">
          Nepodarilo sa načítať dostupné ubytovanie. Skúste to prosím znova.
        </p>
        <button
          type="button"
          onClick={() => onGoToStep(1)}
          className="text-sm font-bold text-secondary underline underline-offset-2"
        >
          Späť na výber termínu
        </button>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-4 rounded-[24px] border border-border p-6 text-center">
        <p className="text-base text-secondary">
          Pre zvolený termín a počet hostí nie je momentálne dostupné žiadne ubytovanie. Skúste
          prosím zmeniť termín alebo počet hostí.
        </p>
        <button
          type="button"
          onClick={() => onGoToStep(1)}
          className="text-sm font-bold text-secondary underline underline-offset-2"
        >
          Zmeniť termín
        </button>
      </div>
    );
  }

  return (
    <div className="grid w-full grid-cols-12 gap-4">
      <FormField
        control={form.control}
        name="property_id"
        render={({ field }) => (
          <FormItem className="col-span-12">
            <div className="grid grid-cols-12 gap-4">
              {properties.map(property => (
                <PropertyOption
                  key={property.id}
                  property={property}
                  isChecked={field.value === property.id}
                  nights={nights}
                  guestsCount={guestsCount}
                  onSelect={() => field.onChange(property.id)}
                />
              ))}
            </div>

            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

interface PropertyOptionProps {
  property: HousingProperty;
  isChecked: boolean;
  nights: number;
  guestsCount: number;
  onSelect: () => void;
}

function PropertyOption({
  property,
  isChecked,
  nights,
  guestsCount,
  onSelect,
}: PropertyOptionProps) {
  const locale = useLocaleStore(s => s.currentLocale);

  const { data: propertyDetail } = useQuery({
    queryKey: ['housing-property-detail', property.slug],
    queryFn: () => fetchHousingProperty({ locale, slug: property.slug }).then(r => r.data),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const pricePerNight = computeHousingNightlyPrice({
    propertyId: property.id,
    guestsCount,
    flatPricePerNight: Number(property.price_per_night) || 0,
  });
  const total = nights * pricePerNight;
  const nightsLabel = nights === 1 ? 'noc' : nights <= 4 ? 'noci' : 'nocí';
  const perPerson = Math.round(pricePerNight / guestsCount);
  const priceBreakdown =
    nights > 0
      ? `${nights} ${nightsLabel} × ${pricePerNight} €/noc · ${perPerson} €/os`
      : `${pricePerNight} €/noc · ${perPerson} €/os`;
  const priceDisplay = nights > 0 ? `${total}€` : `od ${pricePerNight}€`;

  return (
    <>
      <div
        className={cn(
          'col-span-12 hidden w-full cursor-pointer items-center justify-between rounded-[24px] border-2 p-4 md:flex lg:p-5',
          isChecked ? 'border-primary  bg-[#FFF9EA]' : 'border-border bg-white',
        )}
        onClick={onSelect}
      >
        <div className="flex w-full items-center justify-start gap-4 lg:gap-5">
          <div className="relative max-h-20 min-h-20 min-w-20 max-w-20 overflow-hidden rounded-[12px]">
            <Image
              src={property?.image?.url}
              alt={resolveImageAlt(property?.image, property?.name)}
              fill
              className=" shrink-0 rounded-[12px] object-cover object-center"
            />
          </div>

          <div className="flex w-full flex-col items-start justify-start gap-[6px]">
            <h5 className="text-left text-lg font-bold tracking-tight text-secondary">
              {property.name}
            </h5>
            <p className="text-left text-base font-normal text-secondary">
              {propertyDetail?.rooms[0]?.housing_type?.name} · až {property?.capacity_total} hostí ·{' '}
              {property?.area} m²
            </p>
          </div>
        </div>
        <div className="flex w-full items-center justify-end gap-4 lg:gap-5">
          <div className="flex w-full flex-col items-end justify-end gap-1">
            <p className="whitespace-nowrap text-right text-2xl font-bold tracking-tight text-secondary">
              {priceDisplay}
            </p>
            <p className="w-full text-right text-sm leading-[130%] text-secondary">
              {priceBreakdown}
            </p>
          </div>
          <CheckCircle isChecked={isChecked} />
        </div>
      </div>
      <div
        className={cn(
          'col-span-12 flex w-full items-center justify-between gap-3 rounded-[24px] border-2 p-3 sm:p-4 md:hidden',
          isChecked ? 'border-primary  bg-[#FFF9EA]' : 'border-border bg-white',
        )}
        onClick={onSelect}
      >
        <div className="flex w-full flex-col items-start justify-start gap-3">
          <div className="flex w-full items-center justify-start gap-3">
            <div className="relative max-h-[70px] min-h-[70px] min-w-[70px] max-w-[70px] overflow-hidden rounded-[10px]">
              <Image
                src={property?.image?.url}
                alt={resolveImageAlt(property?.image, property?.name)}
                fill
                className=" shrink-0 rounded-[12px] object-cover object-center"
              />
            </div>
            <div className="flex w-full flex-col items-start justify-start gap-[6px]">
              <h5 className="text-left text-base font-bold leading-[130%] tracking-tight text-secondary">
                {property.name}
              </h5>
              <p className="text-left text-sm font-normal leading-[130%] text-secondary">
                {propertyDetail?.rooms[0]?.housing_type?.name} · až {property?.capacity_total} hostí
                · {property?.area} m²
              </p>
            </div>
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <p className="whitespace-nowrap text-left text-lg font-bold tracking-tight text-secondary">
              {priceDisplay}
            </p>
            <p className="w-full text-left text-sm leading-[130%] text-secondary">
              {priceBreakdown}
            </p>
          </div>
        </div>
        <CheckCircle isChecked={isChecked} />
      </div>
    </>
  );
}

function CheckCircle({ isChecked }: { isChecked: boolean }) {
  const isDesktop = useBreakpoint('sm');

  return (
    <div
      className={cn(
        'flex max-h-6 min-h-6 min-w-6 max-w-6 items-center justify-center rounded-full border-2 sm:max-h-8 sm:min-h-8 sm:min-w-8 sm:max-w-8',
        isChecked ? 'border-primary bg-primary' : 'border-border bg-white',
      )}
    >
      {isChecked && <FaCheck size={isDesktop ? 16 : 14} className="text-secondary" />}
    </div>
  );
}
